import { GridColDef } from "@material-ui/data-grid";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { buildAccounts } from "../../accounts";
import { EntityDialog } from "../../components/EntityDialog";
import { ChipsCell, EntityTable } from "../../components/EntityTable";
import { IssueCountChip } from "../../components/SetupIssues";
import { PageHeader } from "../../components/PageHeader";
import { companyIssues, membersOfCompany } from "../../setupCheck";
import { useSysadminData } from "../../SysadminData";
import { sysadminPaths } from "../../sysadminUtils";

interface CompanyRow {
    id: string;
    companyName: string;
    companyCvr: string;
    offices: number;
    eaterGroups: number;
    members: number;
    errors: number;
    warnings: number;
    admins: string[];
}

const columns: GridColDef[] = [
    { field: "companyName", headerName: "Name", flex: 1, minWidth: 180 },
    { field: "issues", headerName: "Setup", width: 120, sortable: false, renderCell: p => <IssueCountChip errors={(p.row as CompanyRow).errors} warnings={(p.row as CompanyRow).warnings} /> },
    { field: "offices", headerName: "Offices", type: "number", width: 120 },
    { field: "eaterGroups", headerName: "Eater groups", type: "number", width: 150 },
    { field: "members", headerName: "Members", type: "number", width: 130 },
    {
        field: "admins", headerName: "Admins", flex: 1, minWidth: 220, sortable: false,
        renderCell: p => <ChipsCell values={(p.row as CompanyRow).admins} />,
    },
    { field: "companyCvr", headerName: "CVR", width: 110 },
    { field: "id", headerName: "Id", width: 300, hide: true },
];

/** All companies. Clicking a company selects it and shows its eater groups. */
export const CompaniesTable: React.FC<{ title?: string }> = ({ title }) => {
    const data = useSysadminData();
    const history = useHistory();
    const [creating, setCreating] = useState(false);

    const rows: CompanyRow[] = useMemo(() => {
        const accounts = buildAccounts(data);
        return data.companies.map(({ content }) => {
            const officeIds = data.offices.filter(o => o.content.companyId === content.id).map(o => o.content.id);
            const issues = companyIssues(data, accounts, content.id);
            return {
                id: content.id,
                companyName: content.companyName || "Unnamed company",
                companyCvr: content.companyCvr,
                offices: officeIds.length,
                eaterGroups: data.eaterGroups.filter(g => officeIds.includes(g.content.officeId)).length,
                members: membersOfCompany(accounts, content.id).length,
                errors: issues.filter(i => i.severity === "error").length,
                warnings: issues.filter(i => i.severity === "warning").length,
                admins: content.companyAdminIds ?? [],
            };
        }).sort((a, b) => a.companyName.localeCompare(b.companyName));
    }, [data]);

    function open(id: string) {
        data.setSelectedCompanyId(id);
        history.push(sysadminPaths.list("eaterGroups"));
    }

    return (
        <>
            <EntityTable<CompanyRow>
                title={title}
                rows={rows}
                columns={columns}
                getRowId={row => row.id}
                searchText={row => [row.companyName, row.companyCvr, ...row.admins].join(" ")}
                searchPlaceholder="Search name, CVR, admin"
                onRowClick={row => open(row.id)}
                createLabel="New company"
                onCreate={() => setCreating(true)}
                loading={data.loading && data.companies.length === 0}
                error={data.error}
                onRetry={data.reload}
                emptyText="No companies yet"
            />
            {creating && <EntityDialog target={{ kind: "company" }} onClose={() => setCreating(false)} onSaved={open} />}
        </>
    );
};

export const Companies: React.FC = () => (
    <div>
        <PageHeader title="Companies" subtitle="Click a company to select it and see its eater groups. Setup shows how many problems the company has." />
        <CompaniesTable />
    </div>
);
